import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE } from '../../lib/api';
import { cloneTimerForm, mapTimerSettingsToForm, toIsoStringFromLocalInput, COUNTDOWN_DEFAULT_STYLE } from '../../utils/timerUtils';
import CountdownTimer from '../ui/CountdownTimer';

export default function SystemSettingsModal({ onClose }) {
    const iconScale = {
        progress: 0.9,
        payments: 1,
        'study-materials': 1.4,
        documents: 1.1,
        schedule: 1.35,
        permit: 1.13,
        users: 0.86,
        'question-bank': 0.84,
        settings: 0.84,
        content: 0.84,
    };

    const [activeTab, setActiveTab] = useState("dashboard-order");
    const iconImg = (name, alt) => (
        <img
          src={`/icons/${name}.png`}
          alt={alt}
          className="w-24 h-24 object-contain block"
          style={{ transform: `scale(${iconScale[name] || 1})` }}
          loading="lazy"
        />
    );

    const buildDefaultAdminSections = () => [
        { id: "user-management", title: "User Management", description: "View, edit, and manage student accounts and instructor profiles", icon: iconImg('users', 'User Management') },
        { id: "question-bank", title: "Question Bank", description: "Add, edit, and manage CDL practice questions across all categories", icon: iconImg('question-bank', 'Question Bank') },
        { id: "training-progress", title: "Training Progress", description: "View and manage student training progress across all sections", icon: iconImg('progress', 'Training Progress') },
        { id: "system-settings", title: "System Settings", description: "Configure platform settings, categories, and system preferences", icon: iconImg('settings', 'System Settings') },
        { id: "payments", title: "Payments & Discounts", description: "Review student balances, approve payments, and manage discounts", icon: iconImg('payments', 'Payments & Discounts') },
        { id: "study-materials", title: "Study Materials", description: "Upload, manage, and organize training documents and resources", icon: iconImg('study-materials', 'Study Materials') },
        { id: "student-documents", title: "Student Documents", description: "View and manage documents uploaded by students", icon: iconImg('documents', 'Student Documents') },
        { id: "enrollment-messages", title: "Enrollment Messages", description: "View and manage student enrollment requests", icon: iconImg('permit', 'Enrollment Messages') },
        { id: "content-management", title: "Content Management", description: "Manage website content and announcements", icon: iconImg('content', 'Content Management') },
        { id: "training-calendar", title: "Training Calendar", description: "View weekly calendar organized by trucks with student names and manage appointments", icon: iconImg('schedule', 'Training Calendar') }
    ];

    const buildDefaultStudentSections = () => [
        { id: "permit", title: "CDL Permit Practice", description: "Practice for your CDL permit exam with interactive questions", icon: iconImg('permit', 'Permit practice') },
        { id: "schedule", title: "Schedule Training", description: "Book your hands-on training sessions with instructors", icon: iconImg('schedule', 'Schedule training') },
        { id: "progress", title: "Training Progress", description: "Track your training progress and completion status", icon: iconImg('progress', 'Training progress') },
        { id: "documents", title: "My Documents", description: "Access your training certificates and important documents", icon: iconImg('documents', 'Documents') },
        { id: "study-materials", title: "Study Materials", description: "Access training materials, guides, and resources", icon: iconImg('study-materials', 'Study materials') },
        { id: "payments", title: "Payments & Discounts", description: "View your tuition balance, generate discounts, and submit payments for approval", icon: iconImg('payments', 'Payments & Discounts') }
    ];

    const [adminSections, setAdminSections] = useState(buildDefaultAdminSections);
    const [studentSections, setStudentSections] = useState(buildDefaultStudentSections);

    const [timerForm, setTimerForm] = useState(() => cloneTimerForm(mapTimerSettingsToForm({})));
    const [timerOriginal, setTimerOriginal] = useState(() => cloneTimerForm(mapTimerSettingsToForm({})));
    const [timerLoading, setTimerLoading] = useState(true);
    const [timerSaving, setTimerSaving] = useState(false);
    const [timerError, setTimerError] = useState('');
    const [timerSuccess, setTimerSuccess] = useState('');

    const timerDirty = useMemo(() => JSON.stringify(timerForm) !== JSON.stringify(timerOriginal), [timerForm, timerOriginal]);

    const previewTargetDate = useMemo(() => {
        if (timerForm?.targetDate) {
            const iso = toIsoStringFromLocalInput(timerForm.targetDate);
            if (iso) return iso;
        }

        const fallback = new Date();
        fallback.setDate(fallback.getDate() + 7);
        fallback.setHours(9, 0, 0, 0);
        return fallback.toISOString();
    }, [timerForm?.targetDate]);

    // Load saved order from localStorage
    useEffect(() => {
        const savedAdminOrder = localStorage.getItem('admin-dashboard-order');
        const savedStudentOrder = localStorage.getItem('student-dashboard-order');

        if (savedAdminOrder) {
            try {
                const order = JSON.parse(savedAdminOrder);
                if (Array.isArray(order)) {
                    const orderedSections = order
                        .map(id => adminSections.find(s => s.id === id))
                        .filter(Boolean);
                    const missingSections = adminSections.filter(section => !order.includes(section.id));
                    setAdminSections([...orderedSections, ...missingSections]);
                }
            } catch (error) {
                console.error('Failed to parse admin dashboard order:', error);
            }
        }

        if (savedStudentOrder) {
            try {
                const order = JSON.parse(savedStudentOrder);
                if (Array.isArray(order)) {
                    const orderedSections = order
                        .map(id => studentSections.find(s => s.id === id))
                        .filter(Boolean);
                    const missingSections = studentSections.filter(section => !order.includes(section.id));
                    setStudentSections([...orderedSections, ...missingSections]);
                }
            } catch (error) {
                console.error('Failed to parse student dashboard order:', error);
            }
        }
    }, []);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();

        const loadTimerSettings = async () => {
            setTimerLoading(true);
            setTimerError('');

            try {
                const response = await fetch(`${API_BASE}/api/site/timer`, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`Request failed: ${response.status}`);
                }

                const data = await response.json();
                if (!active) return;

                const mapped = mapTimerSettingsToForm(data || {});
                setTimerForm(cloneTimerForm(mapped));
                setTimerOriginal(cloneTimerForm(mapped));
            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error('Failed to load timer settings:', error);
                if (active) {
                    setTimerError('Unable to load timer settings. Please try again.');
                }
            } finally {
                if (active) {
                    setTimerLoading(false);
                }
            }
        };

        loadTimerSettings();

        return () => {
            active = false;
            controller.abort();
        };
    }, []);

    const handleDragStart = (e, index, type) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ index, type }));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, dropIndex, type) => {
        e.preventDefault();
        const data = JSON.parse(e.dataTransfer.getData('text/plain'));

        if (data.type !== type) return;

        const sections = type === 'admin' ? adminSections : studentSections;
        const newSections = [...sections];
        const draggedItem = newSections.splice(data.index, 1)[0];
        newSections.splice(dropIndex, 0, draggedItem);

        if (type === 'admin') {
            setAdminSections(newSections);
            localStorage.setItem('admin-dashboard-order', JSON.stringify(newSections.map(s => s.id)));
        } else {
            setStudentSections(newSections);
            localStorage.setItem('student-dashboard-order', JSON.stringify(newSections.map(s => s.id)));
        }
    };

    const resetToDefault = (type) => {
        if (type === 'admin') {
            const defaultSections = buildDefaultAdminSections();
            setAdminSections(defaultSections);
            localStorage.removeItem('admin-dashboard-order');
        } else {
            const defaultSections = buildDefaultStudentSections();
            setStudentSections(defaultSections);
            localStorage.removeItem('student-dashboard-order');
        }
    };

    const handleTimerFieldChange = (field, value) => {
        setTimerForm(prev => ({
            ...prev,
            [field]: value,
        }));
        setTimerSuccess('');
        setTimerError('');
    };

    const handleTimerStyleChange = (field, value) => {
        setTimerForm(prev => ({
            ...prev,
            style: {
                ...prev.style,
                [field]: value,
            },
        }));
        setTimerSuccess('');
        setTimerError('');
    };

    const handleTimerReset = () => {
        const baseline = timerOriginal ? cloneTimerForm(timerOriginal) : cloneTimerForm(mapTimerSettingsToForm({}));
        setTimerForm(baseline);
        setTimerSuccess('');
        setTimerError('');
    };

    const handleTimerSave = async () => {
        if (timerSaving) return;

        setTimerSaving(true);
        setTimerError('');
        setTimerSuccess('');

        try {
            const headline = (timerForm.text || '').trim() || 'Next CDL class starts in:';
            const targetIso = toIsoStringFromLocalInput(timerForm.targetDate);

            if (timerForm.enabled && !targetIso) {
                setTimerError('Select a valid target date and time when the timer is enabled.');
                setTimerSaving(false);
                return;
            }

            const mergedStyle = { ...COUNTDOWN_DEFAULT_STYLE, ...(timerForm.style || {}) };
            const stylePayload = {};

            Object.entries(mergedStyle).forEach(([key, value]) => {
                if (typeof value === 'string') {
                    stylePayload[key] = value.trim();
                } else if (typeof value === 'boolean' || typeof value === 'number') {
                    stylePayload[key] = value;
                }
            });

            if (!stylePayload.badgeText) {
                stylePayload.badgeText = COUNTDOWN_DEFAULT_STYLE.badgeText;
            }
            if (!stylePayload.expireMessage) {
                stylePayload.expireMessage = COUNTDOWN_DEFAULT_STYLE.expireMessage;
            }

            const payload = {
                timer_enabled: Boolean(timerForm.enabled),
                timer_text: headline,
                expire_action: timerForm.expireAction,
                target_date: targetIso,
                style_json: stylePayload,
            };

            const token = localStorage.getItem('go2cdl_access_token');
            const response = await fetch(`${API_BASE}/api/admin/site/timer`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const message = errorData?.error || 'Failed to save timer settings.';
                throw new Error(message);
            }

            const updated = await response.json();
            const mapped = mapTimerSettingsToForm(updated || {});
            const cloned = cloneTimerForm(mapped);
            setTimerForm(cloned);
            setTimerOriginal(cloneTimerForm(mapped));
            setTimerSuccess('Countdown timer saved.');

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('timer-settings:updated', { detail: updated }));
            }
        } catch (error) {
            console.error('Failed to save timer settings:', error);
            setTimerError(error.message || 'Failed to save timer settings.');
        } finally {
            setTimerSaving(false);
        }
    };

    const renderColorInput = (label, field, placeholder) => {
        const value = timerForm.style?.[field] || '';
        return (
            <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</label>
                <div className="mt-2 flex items-center gap-3">
                    <span
                        className="h-8 w-8 rounded-full border border-slate-200 shadow-inner"
                        style={{ background: value || '#ffffff' }}
                        aria-hidden="true"
                    />
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleTimerStyleChange(field, e.target.value)}
                        className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
                        placeholder={placeholder}
                        autoComplete="off"
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 pt-20">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">System Settings</h2>
                    </div>
                    <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
                </div>

                <div className="p-6">
                    {/* Tab Navigation */}
                    <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setActiveTab("dashboard-order")}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === "dashboard-order"
                                ? "bg-white text-gray-900 shadow-sm"
                                : "text-gray-600 hover:text-gray-900"
                                }`}
                        >
                            Dashboard Order
                        </button>
                        <button
                            onClick={() => setActiveTab("general")}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === "general"
                                ? "bg-white text-gray-900 shadow-sm"
                                : "text-gray-600 hover:text-gray-900"
                                }`}
                        >
                            General Settings
                        </button>
                    </div>

                    {/* Dashboard Order Tab */}
                    {activeTab === "dashboard-order" && (
                        <div className="space-y-8">
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Admin Dashboard Order</h3>
                                    <button
                                        onClick={() => resetToDefault('admin')}
                                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                                    >
                                        Reset to Default
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {adminSections.map((section, index) => (
                                        <div
                                            key={section.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index, 'admin')}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, index, 'admin')}
                                            className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-move hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="text-gray-400 mr-3">⋮⋮</div>
                                            <div className="text-lg mr-3">{section.icon}</div>
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900">{section.title}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Student Dashboard Order</h3>
                                    <button
                                        onClick={() => resetToDefault('student')}
                                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                                    >
                                        Reset to Default
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {studentSections.map((section, index) => (
                                        <div
                                            key={section.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index, 'student')}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, index, 'student')}
                                            className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-move hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="text-gray-400 mr-3">⋮⋮</div>
                                            <div className="text-lg mr-3">{section.icon}</div>
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900">{section.title}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    )}

                    {/* General Settings Tab */}
                    {activeTab === "general" && (
                        <div className="space-y-6">
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-700">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <h3 className="text-base font-semibold text-slate-900">Homepage Countdown Timer</h3>
                                        <p>Manage the countdown that appears between the navigation and hero image.</p>
                                    </div>
                                    <div className="text-xs text-slate-500">Auto-refreshes every second for visitors.</div>
                                </div>
                            </div>

                            <div className="space-y-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <h4 className="text-lg font-semibold text-slate-900">Timer visibility & schedule</h4>
                                        <p className="text-sm text-slate-600">Set the headline, target date, and what happens when time runs out.</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-sm font-medium ${timerForm.enabled ? 'text-emerald-600' : 'text-slate-500'}`}>
                                            {timerForm.enabled ? 'Active' : 'Hidden'}
                                        </span>
                                        <label className="relative inline-flex h-6 w-11 cursor-pointer items-center">
                                            <input
                                                type="checkbox"
                                                className="peer sr-only"
                                                checked={timerForm.enabled}
                                                onChange={(e) => handleTimerFieldChange('enabled', e.target.checked)}
                                            />
                                            <span className="absolute inset-0 rounded-full bg-slate-300 transition peer-checked:bg-emerald-500" />
                                            <span className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5" />
                                        </label>
                                    </div>
                                </div>

                                {timerError && (
                                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                        {timerError}
                                    </div>
                                )}

                                {timerSuccess && (
                                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                                        {timerSuccess}
                                    </div>
                                )}

                                {timerLoading ? (
                                    <div className="space-y-4">
                                        <div className="h-4 w-1/3 animate-pulse rounded bg-slate-100" />
                                        <div className="h-32 w-full animate-pulse rounded-lg bg-slate-100" />
                                        <div className="h-48 w-full animate-pulse rounded-lg bg-slate-100" />
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid gap-6 lg:grid-cols-2">
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="text-sm font-medium text-slate-700">Headline text</label>
                                                    <input
                                                        type="text"
                                                        value={timerForm.text}
                                                        onChange={(e) => handleTimerFieldChange('text', e.target.value)}
                                                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                                        placeholder="Next CDL class starts in:"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-slate-700">Target date & time</label>
                                                    <input
                                                        type="datetime-local"
                                                        value={timerForm.targetDate || ''}
                                                        onChange={(e) => handleTimerFieldChange('targetDate', e.target.value)}
                                                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                                    />
                                                    <p className="mt-1 text-xs text-slate-500">Times are saved using your local timezone.</p>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-slate-700">When the countdown ends</label>
                                                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                                        <label className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm transition ${timerForm.expireAction === 'hide' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                                                            <input
                                                                type="radio"
                                                                name="timer-expire-action"
                                                                value="hide"
                                                                checked={timerForm.expireAction === 'hide'}
                                                                onChange={(e) => handleTimerFieldChange('expireAction', e.target.value)}
                                                                className="mt-1"
                                                            />
                                                            <span>Hide the banner</span>
                                                        </label>
                                                        <label className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm transition ${timerForm.expireAction === 'message' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                                                            <input
                                                                type="radio"
                                                                name="timer-expire-action"
                                                                value="message"
                                                                checked={timerForm.expireAction === 'message'}
                                                                onChange={(e) => handleTimerFieldChange('expireAction', e.target.value)}
                                                                className="mt-1"
                                                            />
                                                            <span>Show a custom message</span>
                                                        </label>
                                                    </div>
                                                </div>
                                                {timerForm.expireAction === 'message' && (
                                                    <div>
                                                        <label className="text-sm font-medium text-slate-700">Message after expiration</label>
                                                        <textarea
                                                            value={timerForm.style.expireMessage || ''}
                                                            onChange={(e) => handleTimerStyleChange('expireMessage', e.target.value)}
                                                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                                            rows={3}
                                                            placeholder="The countdown has ended — contact us to reserve your spot!"
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="space-y-4">
                                                <div>
                                                    <label className="text-sm font-medium text-slate-700">Badge text</label>
                                                    <input
                                                        type="text"
                                                        value={timerForm.style.badgeText || ''}
                                                        onChange={(e) => handleTimerStyleChange('badgeText', e.target.value)}
                                                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                                        placeholder="🔥 Limited seats available"
                                                    />
                                                </div>
                                                <div className="grid gap-4 sm:grid-cols-2">
                                                    {renderColorInput('Background', 'backgroundColor', '#0f172a')}
                                                    {renderColorInput('Headline text', 'textColor', '#f8fafc')}
                                                    {renderColorInput('Accent', 'accentColor', '#ef4444')}
                                                    {renderColorInput('Label text', 'labelColor', '#e2e8f0')}
                                                    {renderColorInput('Counter background', 'digitBackground', 'rgba(15, 23, 42, 0.92)')}
                                                    {renderColorInput('Counter text', 'digitTextColor', '#f8fafc')}
                                                    {renderColorInput('Badge background', 'badgeBackground', '#1e293b')}
                                                    {renderColorInput('Badge text', 'badgeTextColor', '#f8fafc')}
                                                </div>
                                                <div className="grid gap-4 sm:grid-cols-2">
                                                    <div>
                                                        <label className="text-sm font-medium text-slate-700">Font family</label>
                                                        <input
                                                            type="text"
                                                            value={timerForm.style.fontFamily || ''}
                                                            onChange={(e) => handleTimerStyleChange('fontFamily', e.target.value)}
                                                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                                            placeholder="Poppins, sans-serif"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium text-slate-700">Border radius</label>
                                                        <input
                                                            type="text"
                                                            value={timerForm.style.borderRadius || ''}
                                                            onChange={(e) => handleTimerStyleChange('borderRadius', e.target.value)}
                                                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                                            placeholder="1rem"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid gap-4 sm:grid-cols-2">
                                                    <div>
                                                        <label className="text-sm font-medium text-slate-700">Layout</label>
                                                        <select
                                                            value={timerForm.style.layout || 'stacked'}
                                                            onChange={(e) => handleTimerStyleChange('layout', e.target.value)}
                                                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                                        >
                                                            <option value="stacked">Stacked (mobile friendly)</option>
                                                            <option value="inline">Inline</option>
                                                        </select>
                                                    </div>
                                                    <div className="flex flex-col gap-3 pt-2">
                                                        <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                                                            <input
                                                                type="checkbox"
                                                                checked={Boolean(timerForm.style.uppercaseLabels)}
                                                                onChange={(e) => handleTimerStyleChange('uppercaseLabels', e.target.checked)}
                                                            />
                                                            Uppercase labels
                                                        </label>
                                                        <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                                                            <input
                                                                type="checkbox"
                                                                checked={Boolean(timerForm.style.showShadow)}
                                                                onChange={(e) => handleTimerStyleChange('showShadow', e.target.checked)}
                                                            />
                                                            Drop shadow
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-t border-slate-200 pt-6">
                                            <p className="text-sm font-medium text-slate-700">Live preview</p>
                                            <CountdownTimer
                                                targetDate={previewTargetDate}
                                                text={timerForm.text || 'Next CDL class starts in:'}
                                                style={timerForm.style}
                                                onExpireAction={timerForm.expireAction}
                                                className="pt-4"
                                            />
                                            {!timerForm.enabled && (
                                                <p className="mt-2 text-xs text-slate-500">The timer is hidden from visitors until it is enabled.</p>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                                            <button
                                                type="button"
                                                onClick={handleTimerReset}
                                                disabled={!timerDirty || timerSaving || timerLoading}
                                                className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                            >
                                                Reset changes
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleTimerSave}
                                                disabled={timerSaving || timerLoading || !timerDirty}
                                                className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
                                            >
                                                {timerSaving ? 'Saving…' : 'Save timer'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
